import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getStoredProjects, updateProjectProposal, deleteProject, requestProjectMoreInfo } from './storageService';
import { getActiveUser, loginUser } from './authService';
import { evaluateClientRequest } from './aiService';

const AdminProposalStudio = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [adminEmail, setAdminEmail] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [authError, setAuthError] = useState('');
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  const [projects, setProjects] = useState([]);
  const [activeTab, setActiveTab] = useState('all'); // 'all' | 'needs_proposal' | 'needs_info' | 'signed' | 'completed'
  const [selectedProject, setSelectedProject] = useState(null);

  // Proposal Studio Form State
  const [hours, setHours] = useState(40);
  const [hourlyRate, setHourlyRate] = useState(110);
  const [materialCost, setMaterialCost] = useState(0);
  const [timelineWeeks, setTimelineWeeks] = useState(4);
  const [paymentPlan, setPaymentPlan] = useState('3-stage'); // '3-stage' | '50-50' | 'full'
  const [executiveSummary, setExecutiveSummary] = useState('');
  const [deliverablesText, setDeliverablesText] = useState('');
  const [notification, setNotification] = useState('');

  // Lifecycle & AI Studio Modals
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [showNeedInfoModal, setShowNeedInfoModal] = useState(false);
  const [needInfoQuestions, setNeedInfoQuestions] = useState('');
  const [needInfoNotes, setNeedInfoNotes] = useState('');
  const [aiEvalResult, setAiEvalResult] = useState(null);
  const [isEvaluatingAI, setIsEvaluatingAI] = useState(false);

  const loadProjects = () => {
    const list = getStoredProjects();
    setProjects(list);
  };

  useEffect(() => {
    const active = getActiveUser();
    if (active?.role === 'admin') {
      setIsAuthenticated(true);
      setAdminEmail(active.email);
    }
    loadProjects();
    window.addEventListener('projects_updated', loadProjects);
    return () => window.removeEventListener('projects_updated', loadProjects);
  }, []);

  const handleAccountLogin = async (e) => {
    e.preventDefault();
    setIsLoggingIn(true);
    setAuthError('');
    try {
      const res = await loginUser(adminEmail.trim(), adminPassword);
      if (res.success && res.user.role === 'admin') {
        setIsAuthenticated(true);
        setAuthError('');
      } else if (res.success) {
        setAuthError('Access denied. Administrator privileges required.');
      } else {
        setAuthError(res.error || 'Invalid administrator email or password.');
      }
    } catch (err) {
      setAuthError('Authentication error. Please try again.');
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleSelectProject = (proj) => {
    setSelectedProject(proj);
    setAiEvalResult(null);
    if (proj.proposal) {
      setHours(proj.proposal.estimatedHours || 40);
      setHourlyRate(proj.proposal.hourlyRate || 110);
      setMaterialCost(proj.proposal.materialCost || 0);
      setTimelineWeeks(proj.proposal.estimatedWeeks || 4);
      setPaymentPlan(proj.proposal.paymentPlan || '3-stage');
      setExecutiveSummary(proj.proposal.executiveSummary || '');
      setDeliverablesText(proj.proposal.deliverables?.join('\n') || '');
    } else {
      // Auto-generate enterprise software defaults
      setHourlyRate(110);
      setHours(45);
      setMaterialCost(0);
      setTimelineWeeks(4);
      setPaymentPlan('3-stage');
      generateSmartProposalDraft(proj);
    }
  };

  const generateSmartProposalDraft = (proj) => {
    setExecutiveSummary(`Guru's Workshop LLC will architect and deliver a high-velocity solution for "${proj.title}". We design scalable, modular architectures (React/Node/Cloud Native) adhering to zero-downtime reliability standards and permanent $0/month baseline infrastructure economics.`);
    setDeliverablesText([
      'Technical Architecture Specification & Data Flow Design',
      'Full-Stack Frontend & Responsive Mobile Layouts',
      'Secure API & Data Layer with Role-Based Session Handling',
      'Staging Environment Deployment & Live Demo Testing',
      'Final DNS Cutover, CI/CD Automated Pipelines, and 30-day Post-Launch Warranty'
    ].join('\n'));
  };

  const calculateTotal = () => {
    return (hours * hourlyRate) + Number(materialCost);
  };

  const handleDeleteProject = (id) => {
    deleteProject(id);
    setDeleteConfirmId(null);
    if (selectedProject?.id === id) {
      setSelectedProject(null);
    }
    loadProjects();
    setNotification(`🗑️ Request ${id} has been permanently deleted.`);
    setTimeout(() => setNotification(''), 4000);
  };

  const handleSendNeedMoreInfo = (e) => {
    e.preventDefault();
    if (!selectedProject) return;

    const questionsList = needInfoQuestions
      .split('\n')
      .map(q => q.trim().replace(/^[-*•\d.)\s]+/, ''))
      .filter(q => q.length > 0);

    const updated = requestProjectMoreInfo(
      selectedProject.id,
      questionsList.length > 0 ? questionsList : ['Please provide additional technical specifications or constraints.'],
      needInfoNotes.trim()
    );

    if (updated) {
      setSelectedProject(updated);
      loadProjects();
      setShowNeedInfoModal(false);
      setNeedInfoQuestions('');
      setNeedInfoNotes('');
      setNotification(`⚠️ Clarification request sent to ${updated.client.name}! Status updated to "Needs More Info".`);
      setTimeout(() => setNotification(''), 4500);
    }
  };

  const handleRunAIEvaluation = () => {
    if (!selectedProject) return;
    setIsEvaluatingAI(true);
    setTimeout(() => {
      const evaluation = evaluateClientRequest(selectedProject);
      setAiEvalResult(evaluation);
      setIsEvaluatingAI(false);
    }, 350);
  };

  const handleApplyAIRecommendations = () => {
    if (!aiEvalResult) return;
    if (aiEvalResult.estimatedHoursRange?.suggestedHours) {
      setHours(aiEvalResult.estimatedHoursRange.suggestedHours);
    }
    if (aiEvalResult.suggestedWeeklyTimeline) {
      setTimelineWeeks(aiEvalResult.suggestedWeeklyTimeline);
    }
    if (aiEvalResult.executiveSOWSummary) {
      setExecutiveSummary(aiEvalResult.executiveSOWSummary);
    }
    if (aiEvalResult.recommendedDeliverables && aiEvalResult.recommendedDeliverables.length > 0) {
      setDeliverablesText(aiEvalResult.recommendedDeliverables.join('\n'));
    }
    setNotification('⚡ AI recommendations successfully applied to SOW draft!');
    setTimeout(() => setNotification(''), 4000);
  };

  const handleAutoPopulateQuestions = () => {
    if (!selectedProject) return;
    const evalData = aiEvalResult || evaluateClientRequest(selectedProject);
    if (evalData.clarifyingQuestions && evalData.clarifyingQuestions.length > 0) {
      setNeedInfoQuestions(evalData.clarifyingQuestions.join('\n'));
    }
  };

  const handlePublishProposal = (e) => {
    e.preventDefault();
    if (!selectedProject) return;

    const total = calculateTotal();
    const deliverablesList = deliverablesText.split('\n').filter(d => d.trim().length > 0);

    let milestones = [];
    if (paymentPlan === '3-stage') {
      const p1 = Math.round(total * 0.40);
      const p2 = Math.round(total * 0.30);
      const p3 = total - p1 - p2;
      milestones = [
        {
          name: 'Milestone 1: Kickoff & Technical Architecture Deposit',
          percentage: 40,
          amount: p1,
          status: 'Pending Payment',
          trigger: 'Unlocks project kickoff, UX wireframes, and database/material procurement.'
        },
        {
          name: 'Milestone 2: Functional Midpoint Staging Demo',
          percentage: 30,
          amount: p2,
          status: 'Locked',
          trigger: 'Released upon client inspection and approval of functional working prototype or dry-fit assembly.'
        },
        {
          name: 'Milestone 3: Final Production Handover & Delivery',
          percentage: 30,
          amount: p3,
          status: 'Locked',
          trigger: 'Released upon production DNS launch, repository handover, or completed physical delivery.'
        }
      ];
    } else if (paymentPlan === '50-50') {
      const p1 = Math.round(total * 0.50);
      const p2 = total - p1;
      milestones = [
        {
          name: 'Milestone 1: Project Kickoff & Materials Deposit',
          percentage: 50,
          amount: p1,
          status: 'Pending Payment',
          trigger: 'Initiates project execution, initial design/code sprint, or lumber milling.'
        },
        {
          name: 'Milestone 2: Final Verification & Handover',
          percentage: 50,
          amount: p2,
          status: 'Locked',
          trigger: 'Released upon successful deployment, demo verification, or final shipment.'
        }
      ];
    } else {
      milestones = [
        {
          name: 'Full Balance on Handover',
          percentage: 100,
          amount: total,
          status: 'Pending Payment',
          trigger: 'Payable upon final inspection and delivery.'
        }
      ];
    }

    const proposalData = {
      executiveSummary,
      estimatedHours: hours,
      hourlyRate,
      materialCost: Number(materialCost),
      totalEstimate: total,
      estimatedWeeks: timelineWeeks,
      deliverables: deliverablesList,
      paymentPlan,
      milestones
    };

    updateProjectProposal(selectedProject.id, proposalData);
    setNotification(`✅ Proposal published for ${selectedProject.id}! Client can now review and sign off.`);
    setTimeout(() => setNotification(''), 4500);
  };

  const filteredProjects = projects.filter(p => {
    if (activeTab === 'needs_proposal') return !p.proposal;
    if (activeTab === 'needs_info') return p.status === 'Needs More Info';
    if (activeTab === 'signed') return p.status === 'Signed & In Production';
    if (activeTab === 'completed') return p.status === 'Completed';
    return true;
  });

  if (!isAuthenticated) {
    return (
      <div style={{ background: 'var(--bg-secondary)', minHeight: '85vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem' }}>
        <div className="workshop-card" style={{ maxWidth: '460px', width: '100%', padding: '2.5rem', backgroundColor: '#ffffff', textAlign: 'center' }}>
          <div style={{ fontSize: '2.5rem', marginBottom: '0.8rem' }}>🔐</div>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 800, marginBottom: '0.4rem', color: '#0f172a' }}>
            Workshop Admin Studio
          </h2>
          <p style={{ color: '#64748b', fontSize: '0.88rem', marginBottom: '1.5rem', lineHeight: 1.5 }}>
            Internal workbench to review client inquiries, evaluate architecture risks, and publish milestone SOW proposals.
          </p>

          <form onSubmit={handleAccountLogin}>
            <div style={{ marginBottom: '1rem', textAlign: 'left' }}>
              <label style={{ display: 'block', fontSize: '0.84rem', fontWeight: 600, color: '#475569', marginBottom: '0.3rem' }}>
                Administrator Email
              </label>
              <input
                type="email"
                className="workshop-input"
                placeholder="name@gurusworkshop.com"
                value={adminEmail}
                onChange={(e) => setAdminEmail(e.target.value)}
                required
                autoFocus
              />
            </div>

            <div style={{ marginBottom: '1.2rem', textAlign: 'left' }}>
              <label style={{ display: 'block', fontSize: '0.84rem', fontWeight: 600, color: '#475569', marginBottom: '0.3rem' }}>
                Password
              </label>
              <input
                type="password"
                className="workshop-input"
                placeholder="Enter account password"
                value={adminPassword}
                onChange={(e) => setAdminPassword(e.target.value)}
                required
              />
              {authError && (
                <div style={{ color: '#ef4444', fontSize: '0.82rem', marginTop: '0.4rem', fontWeight: 600 }}>
                  ⚠️ {authError}
                </div>
              )}
            </div>

            <button
              type="submit"
              disabled={isLoggingIn}
              className="btn-primary-blue"
              style={{ width: '100%', justifyContent: 'center', padding: '0.85rem', fontWeight: 700 }}
            >
              {isLoggingIn ? 'Verifying Credentials...' : 'Authenticate & Open Studio \u2192'}
            </button>
          </form>

          <div style={{ marginTop: '1.6rem', borderTop: '1px solid #f1f5f9', paddingTop: '1rem', fontSize: '0.76rem', color: '#94a3b8' }}>
            🔒 256-bit Web Crypto Authentication • All administrative sessions are logged
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: 'var(--bg-secondary)', color: 'var(--text-primary)', minHeight: '100vh', paddingTop: '2.5rem', paddingBottom: '6rem' }}>
      <div className="container-fluid-cockpit">
        {notification && (
          <div style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            zIndex: 9999,
            backgroundColor: '#059669',
            color: '#ffffff',
            padding: '1rem 1.6rem',
            borderRadius: '12px',
            boxShadow: '0 10px 30px rgba(5, 150, 105, 0.3)',
            fontWeight: 700
          }}>
            {notification}
          </div>
        )}

        {/* Studio Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem', marginBottom: '2.2rem' }}>
          <div>
            <div className="pill-badge pill-blue" style={{ marginBottom: '0.4rem' }}>
              WORKSHOP STUDIO &bull; ADMIN PROPOSAL WORKBENCH
            </div>
            <h1 style={{ fontSize: '2.2rem', margin: 0, fontWeight: 900 }}>
              Client Inquiries &amp; SOW Generation
            </h1>
            <div style={{ color: 'var(--text-secondary)', fontSize: '0.94rem' }}>
              Review project intakes, calculate hours and materials, and publish deliverable-backed proposals.
            </div>
          </div>

          <div style={{ display: 'flex', gap: '0.8rem', alignItems: 'center' }}>
            <Link to="/orders" className="btn-outline-blue" style={{ padding: '0.55rem 1rem', fontSize: '0.88rem' }}>
              📊 Pipeline Ledgers
            </Link>
            <button
              onClick={() => {
                sessionStorage.removeItem('gw_admin_auth');
                setIsAuthenticated(false);
              }}
              style={{
                background: 'transparent',
                border: '1px solid var(--border-subtle)',
                color: '#64748b',
                padding: '0.55rem 1rem',
                borderRadius: '8px',
                fontSize: '0.85rem',
                cursor: 'pointer'
              }}
            >
              Lock Studio 🔒
            </button>
          </div>
        </div>

        {/* Main 2-Column Studio Layout */}
        <div className="row">
          {/* Left Column: Project Inquiries List */}
          <div className="col-lg-5 mb-4 mb-lg-0">
            <div className="workshop-card" style={{ padding: '1.5rem', backgroundColor: '#ffffff' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.2rem' }}>
                <h3 style={{ fontSize: '1.15rem', margin: 0, fontWeight: 800 }}>
                  Incoming Build Requests ({filteredProjects.length})
                </h3>
              </div>

              {/* Filter Tabs */}
              <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginBottom: '1.2rem' }}>
                {[
                  { id: 'all', label: 'All' },
                  { id: 'needs_proposal', label: 'Needs Quote' },
                  { id: 'needs_info', label: 'Needs Info ⚠️' },
                  { id: 'signed', label: 'Signed / Active' },
                  { id: 'completed', label: 'Completed' }
                ].map((t) => (
                  <button
                    key={t.id}
                    onClick={() => setActiveTab(t.id)}
                    className={`tab-btn ${activeTab === t.id ? 'active' : ''}`}
                    style={{ fontSize: '0.78rem', padding: '0.35rem 0.65rem' }}
                  >
                    {t.label}
                  </button>
                ))}
              </div>

              {/* Projects List */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem', maxHeight: '680px', overflowY: 'auto' }}>
                {filteredProjects.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '3rem 1rem', color: '#94a3b8' }}>
                    No requests matching this filter.
                  </div>
                ) : (
                  filteredProjects.map((p) => {
                    const isSelected = selectedProject?.id === p.id;
                    const isNeedsInfo = p.status === 'Needs More Info';
                    const isSigned = p.status === 'Signed & In Production';
                    const hasClarificationReply = Boolean(p.clarification?.clientResponse);

                    return (
                      <div
                        key={p.id}
                        onClick={() => handleSelectProject(p)}
                        style={{
                          padding: '1rem',
                          borderRadius: '10px',
                          border: isSelected ? '2px solid #1d4ed8' : isNeedsInfo ? '1px solid #fde68a' : '1px solid #e2e8f0',
                          backgroundColor: isSelected ? '#eff6ff' : isNeedsInfo ? '#fffdf5' : '#f8fafc',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          position: 'relative'
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.3rem' }}>
                          <span style={{ fontFamily: 'monospace', fontWeight: 800, fontSize: '0.88rem', color: 'var(--accent-blue)' }}>
                            {p.id}
                          </span>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                            <span style={{
                              fontSize: '0.72rem',
                              fontWeight: 700,
                              padding: '0.15rem 0.5rem',
                              borderRadius: '999px',
                              backgroundColor: isNeedsInfo ? '#fef3c7' : isSigned ? '#dbeafe' : p.proposal ? '#ecfdf5' : '#fffbeb',
                              color: isNeedsInfo ? '#b45309' : isSigned ? '#1d4ed8' : p.proposal ? '#059669' : '#b45309',
                              border: isNeedsInfo ? '1px solid #fde68a' : 'none'
                            }}>
                              {p.status}
                            </span>
                            <button
                              type="button"
                              title="Delete this request"
                              onClick={(e) => {
                                e.stopPropagation();
                                setDeleteConfirmId(p.id);
                              }}
                              style={{
                                background: 'transparent',
                                border: 'none',
                                color: '#94a3b8',
                                cursor: 'pointer',
                                fontSize: '0.85rem',
                                padding: '2px 4px',
                                borderRadius: '4px'
                              }}
                              onMouseEnter={(e) => { e.currentTarget.style.color = '#ef4444'; }}
                              onMouseLeave={(e) => { e.currentTarget.style.color = '#94a3b8'; }}
                            >
                              🗑️
                            </button>
                          </div>
                        </div>

                        <div style={{ fontWeight: 700, fontSize: '0.92rem', color: '#0f172a', marginBottom: '0.2rem' }}>
                          {p.title}
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div style={{ fontSize: '0.8rem', color: '#64748b' }}>
                            {p.client?.name} &bull; {p.category}
                          </div>
                          {hasClarificationReply && (
                            <span style={{ fontSize: '0.7rem', color: '#059669', fontWeight: 700, backgroundColor: '#ecfdf5', padding: '1px 6px', borderRadius: '4px' }}>
                              💬 Client Replied
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>

          {/* Right Column: Proposal Engineering & Pricing Studio */}
          <div className="col-lg-7">
            {!selectedProject ? (
              <div className="workshop-card" style={{ padding: '5rem 2rem', textAlign: 'center', backgroundColor: '#ffffff' }}>
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📋</div>
                <h3 style={{ fontSize: '1.4rem', color: '#0f172a' }}>Select a Request to Scope</h3>
                <p style={{ color: '#64748b', maxWidth: '440px', margin: '0 auto' }}>
                  Click on any incoming project from the left queue to calculate hours, set milestone payments, and publish an official proposal.
                </p>
              </div>
            ) : (
              <div className="workshop-card" style={{ padding: '2.2rem', backgroundColor: '#ffffff' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '0.8rem', marginBottom: '1.5rem', borderBottom: '1px solid #e2e8f0', paddingBottom: '1.2rem' }}>
                  <div>
                    <div className="pill-badge pill-blue" style={{ marginBottom: '0.4rem' }}>
                      CRAFTING SOW &bull; {selectedProject.id}
                    </div>
                    <h3 style={{ fontSize: '1.4rem', margin: 0, fontWeight: 800 }}>
                      {selectedProject.title}
                    </h3>
                    <div style={{ fontSize: '0.85rem', color: '#64748b', marginTop: '0.2rem' }}>
                      Client: <strong>{selectedProject.client?.name}</strong> ({selectedProject.client?.email})
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                    <Link
                      to={`/project/${selectedProject.id}`}
                      target="_blank"
                      style={{ fontSize: '0.82rem', color: 'var(--accent-blue)', textDecoration: 'none', fontWeight: 700, padding: '0.4rem 0.7rem', border: '1px solid #bfdbfe', borderRadius: '6px' }}
                    >
                      Tracker ↗
                    </Link>
                    <button
                      type="button"
                      onClick={() => {
                        handleAutoPopulateQuestions();
                        setShowNeedInfoModal(true);
                      }}
                      style={{
                        backgroundColor: '#fffbeb',
                        border: '1px solid #fde68a',
                        color: '#b45309',
                        padding: '0.4rem 0.8rem',
                        borderRadius: '6px',
                        fontSize: '0.82rem',
                        fontWeight: 700,
                        cursor: 'pointer'
                      }}
                    >
                      ⚠️ Request More Info
                    </button>
                    <button
                      type="button"
                      onClick={() => setDeleteConfirmId(selectedProject.id)}
                      style={{
                        backgroundColor: '#fef2f2',
                        border: '1px solid #fecaca',
                        color: '#ef4444',
                        padding: '0.4rem 0.8rem',
                        borderRadius: '6px',
                        fontSize: '0.82rem',
                        fontWeight: 700,
                        cursor: 'pointer'
                      }}
                    >
                      🗑️ Delete
                    </button>
                  </div>
                </div>

                {/* Client Clarification Q&A History if exists */}
                {selectedProject.clarification && (
                  <div style={{
                    backgroundColor: selectedProject.clarification.clientResponse ? '#ecfdf5' : '#fffbeb',
                    border: `1px solid ${selectedProject.clarification.clientResponse ? '#a7f3d0' : '#fde68a'}`,
                    padding: '1rem 1.2rem',
                    borderRadius: '10px',
                    marginBottom: '1.5rem',
                    fontSize: '0.88rem'
                  }}>
                    <div style={{ fontWeight: 800, color: selectedProject.clarification.clientResponse ? '#065f46' : '#92400e', marginBottom: '0.4rem', display: 'flex', justifyContent: 'space-between' }}>
                      <span>
                        {selectedProject.clarification.clientResponse ? '✅ Client Clarification Response Received' : '⏳ Awaiting Client Clarification'}
                      </span>
                      <span style={{ fontSize: '0.75rem', fontWeight: 600 }}>
                        {selectedProject.clarification.requestedAt ? new Date(selectedProject.clarification.requestedAt).toLocaleDateString() : ''}
                      </span>
                    </div>

                    {selectedProject.clarification.questions && selectedProject.clarification.questions.length > 0 && (
                      <div style={{ marginBottom: '0.6rem' }}>
                        <strong style={{ color: '#475569', fontSize: '0.8rem' }}>Questions Asked to Client:</strong>
                        <ul style={{ margin: '0.3rem 0 0 1.2rem', padding: 0, color: '#334155' }}>
                          {selectedProject.clarification.questions.map((q, idx) => (
                            <li key={idx} style={{ marginBottom: '0.2rem' }}>{q}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {selectedProject.clarification.clientResponse && (
                      <div style={{ backgroundColor: '#ffffff', padding: '0.8rem', borderRadius: '8px', border: '1px solid #a7f3d0' }}>
                        <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#059669', marginBottom: '0.2rem' }}>CLIENT RESPONSE:</div>
                        <div style={{ color: '#0f172a', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
                          {selectedProject.clarification.clientResponse}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Client Vision Note */}
                <div style={{ backgroundColor: '#f8fafc', padding: '1rem 1.2rem', borderRadius: '10px', border: '1px solid #e2e8f0', marginBottom: '1.5rem', fontSize: '0.88rem' }}>
                  <div style={{ fontWeight: 700, color: '#475569', marginBottom: '0.3rem' }}>Client Scope Request:</div>
                  <div style={{ color: '#0f172a', lineHeight: '1.5' }}>{selectedProject.description}</div>
                </div>

                {/* AI Request Evaluation Card */}
                <div style={{
                  backgroundColor: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: '12px',
                  padding: '1.2rem',
                  marginBottom: '1.8rem'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem', marginBottom: aiEvalResult ? '1rem' : '0' }}>
                    <div>
                      <div style={{ fontWeight: 800, fontSize: '0.95rem', color: '#0f172a', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                        <span>🤖 AI Request Intelligence &amp; Risk Evaluator</span>
                        <span style={{ fontSize: '0.7rem', padding: '0.1rem 0.45rem', borderRadius: '999px', backgroundColor: '#ecfdf5', color: '#059669', fontWeight: 700 }}>
                          $0 Engine
                        </span>
                      </div>
                      <div style={{ fontSize: '0.8rem', color: '#64748b' }}>
                        Autonomous complexity scoring, risk analysis, and SOW deliverable synthesis.
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={handleRunAIEvaluation}
                      disabled={isEvaluatingAI}
                      style={{
                        backgroundColor: '#1d4ed8',
                        color: '#ffffff',
                        border: 'none',
                        padding: '0.45rem 0.9rem',
                        borderRadius: '8px',
                        fontSize: '0.82rem',
                        fontWeight: 700,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.4rem'
                      }}
                    >
                      {isEvaluatingAI ? 'Evaluating Request...' : aiEvalResult ? '⚡ Re-run AI Analysis' : '✨ Run AI Evaluation'}
                    </button>
                  </div>

                  {aiEvalResult && (
                    <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: '1rem', marginTop: '0.8rem' }}>
                      {/* Metric Badges */}
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '0.6rem', marginBottom: '1rem' }}>
                        <div style={{ backgroundColor: '#ffffff', padding: '0.6rem 0.8rem', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                          <div style={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 600 }}>COMPLEXITY</div>
                          <div style={{ fontSize: '1rem', fontWeight: 800, color: aiEvalResult.complexityScore === 'Enterprise' ? '#b45309' : '#1d4ed8' }}>
                            {aiEvalResult.complexityScore}
                          </div>
                        </div>

                        <div style={{ backgroundColor: '#ffffff', padding: '0.6rem 0.8rem', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                          <div style={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 600 }}>RECOMMENDED HOURS</div>
                          <div style={{ fontSize: '1rem', fontWeight: 800, color: '#0f172a' }}>
                            {aiEvalResult.estimatedHoursRange?.min} – {aiEvalResult.estimatedHoursRange?.max} hrs
                          </div>
                        </div>

                        <div style={{ backgroundColor: '#ffffff', padding: '0.6rem 0.8rem', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                          <div style={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 600 }}>TIMELINE</div>
                          <div style={{ fontSize: '1rem', fontWeight: 800, color: '#059669' }}>
                            ~{aiEvalResult.suggestedWeeklyTimeline} Weeks
                          </div>
                        </div>

                        <div style={{ backgroundColor: '#ffffff', padding: '0.6rem 0.8rem', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                          <div style={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 600 }}>$0 BASELINE CLOUD</div>
                          <div style={{ fontSize: '0.88rem', fontWeight: 800, color: '#059669' }}>
                            100% Feasible
                          </div>
                        </div>
                      </div>

                      {/* Risk Factors */}
                      {aiEvalResult.riskFactors && aiEvalResult.riskFactors.length > 0 && (
                        <div style={{ marginBottom: '0.8rem' }}>
                          <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#b45309', textTransform: 'uppercase', marginBottom: '0.3rem' }}>
                            ⚠️ Flagged Architectural Risks &amp; Edge Cases:
                          </div>
                          <ul style={{ margin: 0, paddingLeft: '1.2rem', fontSize: '0.82rem', color: '#475569' }}>
                            {aiEvalResult.riskFactors.map((r, i) => (
                              <li key={i} style={{ marginBottom: '0.2rem' }}>{r}</li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Action to apply to SOW */}
                      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.6rem', marginTop: '1rem', borderTop: '1px dashed #e2e8f0', paddingTop: '0.8rem' }}>
                        <button
                          type="button"
                          onClick={handleApplyAIRecommendations}
                          style={{
                            backgroundColor: '#059669',
                            color: '#ffffff',
                            border: 'none',
                            padding: '0.5rem 1rem',
                            borderRadius: '8px',
                            fontSize: '0.82rem',
                            fontWeight: 800,
                            cursor: 'pointer'
                          }}
                        >
                          ⚡ Apply AI Recommendations to SOW Form Below
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                <form onSubmit={handlePublishProposal}>
                  {/* Estimator Sliders & Rates */}
                  <h4 style={{ fontSize: '1.05rem', fontWeight: 800, marginBottom: '1rem', color: '#0f172a' }}>
                    1. Resource &amp; Investment Sizing
                  </h4>

                  <div className="row mb-3">
                    <div className="col-md-6 mb-3 mb-md-0">
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.3rem', fontSize: '0.88rem', fontWeight: 600 }}>
                        <span>Engineering / Crafting Hours:</span>
                        <span style={{ color: 'var(--accent-blue)', fontWeight: 800 }}>{hours} hrs</span>
                      </div>
                      <input
                        type="range"
                        min="5"
                        max="160"
                        step="5"
                        value={hours}
                        onChange={(e) => setHours(Number(e.target.value))}
                        style={{ width: '100%', accentColor: 'var(--accent-blue)' }}
                      />
                    </div>

                    <div className="col-md-6">
                      <label style={{ display: 'block', fontSize: '0.88rem', fontWeight: 600, marginBottom: '0.3rem' }}>
                        Hourly Rate ($/hr)
                      </label>
                      <input
                        type="number"
                        className="workshop-input"
                        value={hourlyRate}
                        onChange={(e) => setHourlyRate(Number(e.target.value))}
                        style={{ padding: '0.45rem 0.8rem' }}
                      />
                    </div>
                  </div>

                  <div className="row mb-4">
                    <div className="col-md-6 mb-3 mb-md-0">
                      <label style={{ display: 'block', fontSize: '0.88rem', fontWeight: 600, marginBottom: '0.3rem' }}>
                        Materials / Cloud Infrastructure ($)
                      </label>
                      <input
                        type="number"
                        className="workshop-input"
                        value={materialCost}
                        onChange={(e) => setMaterialCost(Number(e.target.value))}
                        placeholder="0"
                        style={{ padding: '0.45rem 0.8rem' }}
                      />
                    </div>

                    <div className="col-md-6">
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.3rem', fontSize: '0.88rem', fontWeight: 600 }}>
                        <span>Target Timeline:</span>
                        <span style={{ color: '#059669', fontWeight: 800 }}>{timelineWeeks} Weeks</span>
                      </div>
                      <input
                        type="range"
                        min="1"
                        max="16"
                        value={timelineWeeks}
                        onChange={(e) => setTimelineWeeks(Number(e.target.value))}
                        style={{ width: '100%', accentColor: '#059669' }}
                      />
                    </div>
                  </div>

                  {/* Payment Structure Selector */}
                  <div style={{ marginBottom: '1.8rem' }}>
                    <label style={{ display: 'block', fontSize: '0.88rem', fontWeight: 600, marginBottom: '0.5rem' }}>
                      Staged Payment Milestone Plan
                    </label>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.6rem' }}>
                      {[
                        { id: '3-stage', label: '3-Stage Milestones', desc: '40% Kickoff / 30% Demo / 30% Handover' },
                        { id: '50-50', label: '50/50 Split', desc: '50% Kickoff / 50% Final Delivery' },
                        { id: 'full', label: '100% on Handover', desc: 'Full payment on delivery' }
                      ].map((plan) => (
                        <button
                          key={plan.id}
                          type="button"
                          onClick={() => setPaymentPlan(plan.id)}
                          className={`tab-btn ${paymentPlan === plan.id ? 'active' : ''}`}
                          style={{ textAlign: 'left', padding: '0.65rem 0.8rem' }}
                        >
                          <div style={{ fontWeight: 800, fontSize: '0.85rem' }}>{plan.label}</div>
                          <div style={{ fontSize: '0.72rem', opacity: 0.85 }}>{plan.desc}</div>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Calculated Investment Card */}
                  <div style={{
                    padding: '1.1rem 1.4rem',
                    backgroundColor: '#eff6ff',
                    borderRadius: '12px',
                    border: '1px solid #bfdbfe',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '1.8rem'
                  }}>
                    <div>
                      <div style={{ fontSize: '0.78rem', color: '#1e40af', fontWeight: 700, textTransform: 'uppercase' }}>
                        Computed Project Investment
                      </div>
                      <div style={{ fontSize: '2rem', fontWeight: 900, color: '#1d4ed8', fontFamily: 'Outfit' }}>
                        ${calculateTotal().toLocaleString()}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right', fontSize: '0.82rem', color: '#3b82f6' }}>
                      {hours} hrs × ${hourlyRate}/hr {materialCost > 0 ? `+ $${materialCost} materials` : ''}
                    </div>
                  </div>

                  {/* Proposal Narrative & Deliverables */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <h4 style={{ fontSize: '1.05rem', fontWeight: 800, margin: 0, color: '#0f172a' }}>
                      2. SOW Narrative &amp; Deliverables
                    </h4>
                    <button
                      type="button"
                      onClick={() => generateSmartProposalDraft(selectedProject)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: 'var(--accent-blue)',
                        fontSize: '0.82rem',
                        fontWeight: 700,
                        cursor: 'pointer'
                      }}
                    >
                      ✨ Regenerate with AI/Rules
                    </button>
                  </div>

                  <div style={{ marginBottom: '1.2rem' }}>
                    <label style={{ display: 'block', fontSize: '0.84rem', color: '#475569', marginBottom: '0.3rem', fontWeight: 600 }}>
                      Executive Strategy Summary
                    </label>
                    <textarea
                      rows="3"
                      required
                      className="workshop-input"
                      value={executiveSummary}
                      onChange={(e) => setExecutiveSummary(e.target.value)}
                    />
                  </div>

                  <div style={{ marginBottom: '1.8rem' }}>
                    <label style={{ display: 'block', fontSize: '0.84rem', color: '#475569', marginBottom: '0.3rem', fontWeight: 600 }}>
                      Itemized Deliverables (one per line)
                    </label>
                    <textarea
                      rows="4"
                      required
                      className="workshop-input"
                      value={deliverablesText}
                      onChange={(e) => setDeliverablesText(e.target.value)}
                    />
                  </div>

                  <button
                    type="submit"
                    className="btn-primary-blue"
                    style={{ width: '100%', justifyContent: 'center', padding: '0.9rem', fontSize: '1rem', fontWeight: 800 }}
                  >
                    Publish Formal Proposal to Client →
                  </button>
                </form>
              </div>
            )}
          </div>
        </div>
        {/* Need More Info Modal */}
        {showNeedInfoModal && selectedProject && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(15, 23, 42, 0.65)',
            backdropFilter: 'blur(4px)',
            zIndex: 10000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '1.5rem'
          }}>
            <div style={{
              backgroundColor: '#ffffff',
              borderRadius: '16px',
              maxWidth: '580px',
              width: '100%',
              padding: '2rem',
              boxShadow: '0 25px 50px -12px rgba(15, 23, 42, 0.25)',
              maxHeight: '90vh',
              overflowY: 'auto'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.2rem' }}>
                <div>
                  <div className="pill-badge" style={{ backgroundColor: '#fef3c7', color: '#b45309', marginBottom: '0.4rem' }}>
                    CLIENT CLARIFICATION LOOP
                  </div>
                  <h3 style={{ fontSize: '1.3rem', fontWeight: 800, margin: 0, color: '#0f172a' }}>
                    Request More Information
                  </h3>
                  <div style={{ fontSize: '0.84rem', color: '#64748b', marginTop: '0.2rem' }}>
                    Target: <strong>{selectedProject.title}</strong> &bull; {selectedProject.client?.name}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setShowNeedInfoModal(false)}
                  style={{ background: 'none', border: 'none', fontSize: '1.4rem', cursor: 'pointer', color: '#94a3b8' }}
                >
                  ×
                </button>
              </div>

              <form onSubmit={handleSendNeedMoreInfo}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.4rem' }}>
                  <label style={{ fontSize: '0.86rem', fontWeight: 700, color: '#334155' }}>
                    Questions for Client (one per line)
                  </label>
                  <button
                    type="button"
                    onClick={handleAutoPopulateQuestions}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: 'var(--accent-blue)',
                      fontSize: '0.78rem',
                      fontWeight: 700,
                      cursor: 'pointer'
                    }}
                  >
                    ✨ Auto-suggest via AI
                  </button>
                </div>
                <textarea
                  rows="5"
                  required
                  placeholder="e.g.&#10;1. What third-party APIs or authentication providers are required?&#10;2. Do you have target brand guidelines or existing wireframes?&#10;3. What is the expected initial user load volume?"
                  className="workshop-input"
                  value={needInfoQuestions}
                  onChange={(e) => setNeedInfoQuestions(e.target.value)}
                  style={{ marginBottom: '1.2rem', fontSize: '0.88rem' }}
                />

                <label style={{ display: 'block', fontSize: '0.86rem', fontWeight: 700, color: '#334155', marginBottom: '0.4rem' }}>
                  Guidance / Context Note (Optional)
                </label>
                <textarea
                  rows="2"
                  placeholder="e.g. This helps us ensure your project adheres strictly to our $0/month serverless architecture baseline."
                  className="workshop-input"
                  value={needInfoNotes}
                  onChange={(e) => setNeedInfoNotes(e.target.value)}
                  style={{ marginBottom: '1.8rem', fontSize: '0.88rem' }}
                />

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.8rem' }}>
                  <button
                    type="button"
                    onClick={() => setShowNeedInfoModal(false)}
                    style={{
                      padding: '0.65rem 1.2rem',
                      borderRadius: '8px',
                      border: '1px solid #cbd5e1',
                      background: '#ffffff',
                      color: '#475569',
                      fontWeight: 600,
                      cursor: 'pointer'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn-primary-blue"
                    style={{ padding: '0.65rem 1.4rem', fontWeight: 700 }}
                  >
                    Send Clarification Request →
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {deleteConfirmId && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(15, 23, 42, 0.65)',
            backdropFilter: 'blur(4px)',
            zIndex: 10000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '1.5rem'
          }}>
            <div style={{
              backgroundColor: '#ffffff',
              borderRadius: '16px',
              maxWidth: '440px',
              width: '100%',
              padding: '2.2rem',
              boxShadow: '0 25px 50px -12px rgba(15, 23, 42, 0.25)',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '2.5rem', marginBottom: '0.8rem' }}>🗑️</div>
              <h3 style={{ fontSize: '1.3rem', fontWeight: 800, margin: '0 0 0.5rem 0', color: '#0f172a' }}>
                Permanently Delete Request?
              </h3>
              <p style={{ color: '#64748b', fontSize: '0.88rem', marginBottom: '1.8rem', lineHeight: 1.5 }}>
                Are you sure you want to permanently delete request <strong>{deleteConfirmId}</strong>? This action cannot be reversed and removes all associated proposals.
              </p>

              <div style={{ display: 'flex', justifyContent: 'center', gap: '0.8rem' }}>
                <button
                  type="button"
                  onClick={() => setDeleteConfirmId(null)}
                  style={{
                    padding: '0.65rem 1.2rem',
                    borderRadius: '8px',
                    border: '1px solid #cbd5e1',
                    background: '#ffffff',
                    color: '#475569',
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => handleDeleteProject(deleteConfirmId)}
                  style={{
                    padding: '0.65rem 1.4rem',
                    borderRadius: '8px',
                    border: 'none',
                    backgroundColor: '#ef4444',
                    color: '#ffffff',
                    fontWeight: 700,
                    cursor: 'pointer'
                  }}
                >
                  Yes, Delete Request
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminProposalStudio;
